import { useSession, useTenant } from '@zhiyue/core';
import React from 'react';
import { createCmsRepository } from './repository';
import type { CmsDatabase, CmsRepository } from './types';

interface BusinessScope {
  brand: string;
  productId?: string;
  siteId?: string;
}

interface CmsContextValue {
  data?: CmsDatabase;
  loading: boolean;
  error?: string;
  repository: CmsRepository;
  scope: BusinessScope;
  setScope: (scope: BusinessScope) => void;
  refresh: () => Promise<void>;
  reset: () => Promise<void>;
  actorName: string;
}

const CmsContext = React.createContext<CmsContextValue | null>(null);

export const CmsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentTenantUuid } = useTenant();
  const { currentUser } = useSession();
  const tenantId = currentTenantUuid || 'default';

  const repository = React.useMemo(() => {
    return createCmsRepository(tenantId);
  }, [tenantId]);

  const scopeKey = `zhiyue-cms-scope:v1:${tenantId}`;
  const [scope, setScopeState] = React.useState<BusinessScope>(() => {
    try {
      return JSON.parse(
        window.localStorage.getItem(scopeKey) || '',
      ) as BusinessScope;
    } catch {
      return { brand: '全部业务' };
    }
  });
  const [data, setData] = React.useState<CmsDatabase>();
  const [loading, setLoading] = React.useState(false);
  const [error] = React.useState<string>();

  const refresh = React.useCallback(async () => {
    setLoading(false);
    setData(undefined);
  }, []);

  React.useEffect(() => {
    setData(undefined);
    setLoading(false);
  }, []);

  const setScope = React.useCallback(
    (next: BusinessScope) => {
      setScopeState(next);
      window.localStorage.setItem(scopeKey, JSON.stringify(next));
    },
    [scopeKey],
  );

  const reset = React.useCallback(async () => {
    setData(undefined);
  }, []);

  const value = React.useMemo<CmsContextValue>(
    () => ({
      data,
      loading,
      error,
      repository,
      scope,
      setScope,
      refresh,
      reset,
      actorName: currentUser?.name || currentUser?.username || '当前用户',
    }),
    [
      actorNameFromUser(currentUser),
      data,
      error,
      loading,
      refresh,
      repository,
      reset,
      scope,
      setScope,
    ],
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
};

const actorNameFromUser = (
  user: ReturnType<typeof useSession>['currentUser'],
) => user?.name || user?.username || '当前用户';

export const useCms = () => {
  const value = React.useContext(CmsContext);
  if (!value) throw new Error('useCms must be used within CmsProvider');
  return value;
};
